/* ════════════════════════ v6: FOCUS — cross-project triage ════════════════════════
   A portfolio-wide work queue. Pools effective (edits-overlay) open actions, open
   threats, and quiet projects across all NON-ARCHIVED projects into urgency buckets.
   READ-AND-JUMP only — rows navigate to the item in its project; no inline editing.
   Hardcoded thresholds (due-this-week 7d, quiet 6wk, top-risk score >= 6). Reads each
   project's OWN c.actions / c.edits / c.risks / c.sessions directly — never the active-
   project globals — and resolves effective values defensively (edit value || base),
   exactly like the overview rollups, so inactive projects are correct too. Bucket
   definitions mirror the per-project pill filters (agenda/overdue/blocked/high-open). */

let _focusPendingTab = null, _focusPendingCard = null;
/* v8.3.0: ephemeral origin of the current project visit — 'focus' when arrived via a
   Focus-view jump, else null (overview). Never serialised, never written to appMeta; it
   only drives the conditional "↩ Focus" return chip in the crumbbar. A cold deep-link or
   reload straight to #/project/<id> leaves it null → the chip stays hidden (correct: no
   Focus context to return to). */
let _projReturn = null;
let _fxFilter = 'all';
/* ── v13.1 (A53): which Focus buckets are folded ───────────────────────────────────
   The fold lived ONLY as a class on nodes that renderFocus() replaces wholesale, so every
   re-render and every return to the view reopened all seven — fold the four you are not
   working on, open one action, come back, and the tidying is gone.
   ⚠ Deliberate stopping point: this is per SESSION, not per machine. The data file is the
   wrong home (Focus is portfolio-wide, and writing a UI preference into the file would
   diverge two colleagues' files — the v11 rule for the last-used programme), and the
   per-machine IndexedDB slot that already holds `lastView` would need an async boot read
   racing the first paint. A reload still opens everything; the register asked for the fold
   to survive a VISIT, and it now does. */
const _fxCollapsed = new Set();

const _FX_BUCKETS = [
  { key:'overdue', title:'Overdue',              sub:'open, past due',               tone:'high',   chip:'red',   label:'overdue' },
  { key:'dueweek', title:'Due this week',        sub:'next 7 days',                  tone:'medium', chip:'amber', label:'due this week' },
  { key:'highpri', title:'High priority · open', sub:'any date',                     tone:'high',   chip:'red',   label:'high priority' },
  { key:'blocked', title:'Blocked',              sub:'waiting on a dependency',      tone:'medium', chip:'amber', label:'blocked' },
  { key:'agenda',  title:'On agenda',            sub:'flagged for upcoming calls',   tone:'low',    chip:'dim',   label:'on agenda' },
  { key:'risks',   title:'Top risks',            sub:'highest-scoring open threats', tone:'high',   chip:'red',   label:'top risks' },
  { key:'quiet',   title:'Quiet projects',       sub:'no session in 6+ weeks',       tone:'dim',    chip:'dim',   label:'quiet projects' },
];

function gotoFocus() { if (_progressPermitRoute('#/focus')) location.hash = '#/focus'; }
function _lbSetSwitch(active) {
  const a = document.getElementById('lbSwitchAccounts'), f = document.getElementById('lbSwitchFocus');
  /* v13.1 (A58): this pair marks selection with 'on', not 'active' — hence the class
     argument. Same one write for the paint and the announced state. */
  _setSelected(a, active === 'accounts', 'on');
  _setSelected(f, active === 'focus', 'on');
}
function showFocus() {
  if (!_progressPermitRoute('#/focus')) return false;
  _pfResetExpansion();
  _progressHelpClose();
  _reorderUiReset();
  renderFocus();
  document.body.classList.remove('view-overview');
  document.body.classList.remove('view-portfolio');   /* v10.5 (W2) */
  document.body.classList.add('view-focus');
  _hdrSearchSync();   /* v16.5 (c): Focus is cross-project and has its own filters - not ours */
  _lbSetSwitch('focus');
  _refreshFileAlerts();   /* v11.1 (W2): after the body class, which gates the strip */
  const dd = document.getElementById('projSwitcher'); if (dd) dd.hidden = true;
  /* v7.0.1: Focus is cross-project too — same title reset as the overview.
     v10.5: Focus stays PROGRAMME-level (it spans projects, never programmes). */
  document.title = _progDocTitle();
  return true;
}
